import { isOnboardingMobileDevice } from "./device";
import {
  onboardingStepsForDevice,
  ONBOARDING_STORAGE_KEY,
  type OnboardingPersisted,
  type OnboardingStep,
  type OnboardingStepId,
} from "./steps";

const LEGACY_STEP_IDS = [
  "timetable-add-topic",
  "timetable-nav-planning",
  "planning-click-lesson",
] as const;

function migrateLegacyStepId(stepId: string, mobile: boolean): OnboardingStepId {
  if (stepId === "timetable-add-topic") {
    return mobile ? "timetable-tap-monday" : "timetable-drag-monday";
  }
  if (stepId === "timetable-nav-planning") {
    return "timetable-click-lesson";
  }
  if (stepId === "planning-click-lesson") {
    return "scheme-add-title";
  }
  return stepId as OnboardingStepId;
}

function normalizeStepId(stepId: string, mobile: boolean): OnboardingStepId {
  const migrated = (LEGACY_STEP_IDS as readonly string[]).includes(stepId)
    ? migrateLegacyStepId(stepId, mobile)
    : (stepId as OnboardingStepId);
  const steps = onboardingStepsForDevice(mobile);
  if (steps.some((s) => s.id === migrated)) {
    return migrated;
  }
  return steps[0].id;
}

export function readOnboarding(): (OnboardingPersisted & { mobile: boolean }) | null {
  try {
    const raw = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OnboardingPersisted;
    if (parsed.status !== "active" && parsed.status !== "done" && parsed.status !== "skipped") {
      return null;
    }
    const mobile = parsed.mobile ?? isOnboardingMobileDevice();
    const stepId = normalizeStepId(parsed.stepId, mobile);
    return { ...parsed, stepId, mobile };
  } catch {
    return null;
  }
}

export function writeOnboarding(state: OnboardingPersisted) {
  localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
}

export function startOnboardingPersisted(mobile: boolean) {
  const steps = onboardingStepsForDevice(mobile);
  writeOnboarding({
    status: "active",
    stepId: steps[0].id,
    focusDate: null,
    mobile,
  });
}

export function stepIndex(stepId: OnboardingStepId, mobile: boolean): number {
  const steps = onboardingStepsForDevice(mobile);
  const idx = steps.findIndex((s) => s.id === stepId);
  return idx >= 0 ? idx : 0;
}

export function numberedOnboardingSteps(mobile: boolean): OnboardingStep[] {
  return onboardingStepsForDevice(mobile).filter((s) => !s.unnumbered);
}

/** 1-based index among numbered steps only (for progress). */
export function numberedStepProgress(stepId: OnboardingStepId, mobile: boolean): number {
  const steps = onboardingStepsForDevice(mobile);
  const idx = stepIndex(stepId, mobile);
  let count = 0;
  for (let i = 0; i < idx; i++) {
    if (!steps[i].unnumbered) count++;
  }
  const current = steps[idx];
  if (current && !current.unnumbered) count++;
  return Math.max(1, count);
}
